import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  ArrowLeft, Search, UserPlus, X, Save, Zap, PackagePlus, FileText,
  MessageCircle, CheckCircle2, ChevronRight, Sparkles, Calendar, MapPin,
  Truck, Wrench, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePageTitle from "@/hooks/use-page-title";
import OrderItemsEditor, { emptyItem, calcItemTotals, calcExtra } from "@/components/OrderItemsEditor";
import ExtrasEditor from "@/components/ExtrasEditor";
import CatalogPicker from "@/components/CatalogPicker";

/* --------------------------- Quick product templates -------------------------- */
const TEMPLATES = [
  { key: "bcard", label: "Business Cards", emoji: "💳", make: () => ({ ...emptyItem(), product_category: "Business Cards", product_name: "Standard Business Card", material: "300 GSM Art Card", printing_type: "Both Sides · 4C", quantity: 100, gst_rate: 18, gst_mode: "excluded", addons: [{ name: "Rate", quantity: 100, rate: 1.5 }] }) },
  { key: "brochure", label: "Brochure", emoji: "📄", make: () => ({ ...emptyItem(), product_category: "Brochures", product_name: "Bi-Fold Brochure", material: "300 GSM Art Card", printing_type: "A4 folded, 4C both sides", quantity: 100, gst_rate: 18, gst_mode: "excluded", addons: [{ name: "Rate", quantity: 100, rate: 7 }] }) },
  { key: "flex", label: "Flex Banner", emoji: "🖼️", make: () => ({ ...emptyItem(), product_category: "Flex Banners", product_name: "Flex Banner (13oz)", material: "13 oz Star Flex", printing_type: "Solvent 720 DPI", gst_rate: 18, gst_mode: "excluded", sizes: [{ width: 4, height: 2, unit: "ft", quantity: 1, rate: 12 }] }) },
  { key: "vinyl", label: "Vinyl Sticker", emoji: "🏷️", make: () => ({ ...emptyItem(), product_category: "Stickers", product_name: "Vinyl Sticker (Die-cut)", material: "Vinyl Glossy", gst_rate: 18, gst_mode: "excluded", sizes: [{ width: 1, height: 1, unit: "ft", quantity: 10, rate: 40 }] }) },
  { key: "acp", label: "ACP Signage", emoji: "🔤", make: () => ({ ...emptyItem(), product_category: "Signage", product_name: "ACP Signboard", material: "3mm ACP", gst_rate: 18, gst_mode: "excluded", sizes: [{ width: 4, height: 2, unit: "ft", quantity: 1, rate: 350 }] }) },
  { key: "standee", label: "Standee", emoji: "📢", make: () => ({ ...emptyItem(), product_category: "Standee", product_name: "Roll-up Standee 6×2 ft", material: "Vinyl on Aluminium Stand", gst_rate: 18, gst_mode: "excluded", quantity: 1, addons: [{ name: "Rate", quantity: 1, rate: 900 }] }) },
  { key: "poster", label: "Poster", emoji: "🖼️", make: () => ({ ...emptyItem(), product_category: "Posters", product_name: "Poster Print", material: "170 GSM Art Paper", printing_type: "4C One Side", gst_rate: 18, gst_mode: "excluded", quantity: 50, addons: [{ name: "Rate", quantity: 50, rate: 25 }] }) },
  { key: "custom", label: "Custom", emoji: "✨", make: () => emptyItem() },
];

export default function NewOrder() {
  usePageTitle("New Order");
  const nav = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const in3Days = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    customer_id: "",
    order_date: today,
    delivery_date: in3Days,
    shipping_address: "",
    transport_charge: 0, transport_gst_rate: 18, transport_gst_mode: "none",
    installation_charge: 0, installation_gst_rate: 18, installation_gst_mode: "none",
    lamination: false, eyelets: false,
    installation_required: false, delivery_required: true,
    priority: "normal", remarks: "",
    advance: 0, advance_mode: "cash", advance_date: today,
  });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // {id, order_no, customer_phone}

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  if (user?.role === "customer") return <Navigate to="/portal" replace />;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const selectedCust = customers.find((c) => c.id === form.customer_id);

  /* Derived totals */
  const itemsSub = items.reduce((s, it) => s + calcItemTotals(it).subtotal, 0);
  const itemsTax = items.reduce((s, it) => s + calcItemTotals(it).tax, 0);
  const tExtra = calcExtra(form.transport_charge, form.transport_gst_rate, form.transport_gst_mode);
  const iExtra = calcExtra(form.installation_charge, form.installation_gst_rate, form.installation_gst_mode);
  const grand = itemsSub + itemsTax + tExtra.total + iExtra.total;
  const balance = Math.max(grand - Number(form.advance || 0), 0);

  const addFromTemplate = (t) => {
    setItems((prev) => [...prev, t.make()]);
    toast.success(`${t.label} added`);
  };

  /* Save */
  const submit = async () => {
    setErr("");
    if (!form.customer_id) { setErr("Please select a customer"); return; }
    if (items.length === 0 || !items.some((i) => calcItemTotals(i).total > 0)) { setErr("Please add at least one product with a rate"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({
          ...it, quantity: Number(it.quantity) || 1,
          gst_rate: Number(it.gst_rate) || 0, gst_mode: it.gst_mode || "excluded",
          sizes: (it.sizes || []).map((s) => ({ width: Number(s.width) || 0, height: Number(s.height) || 0, unit: s.unit || "ft", quantity: Number(s.quantity) || 1, rate: Number(s.rate) || 0 })),
          addons: (it.addons || []).map((a) => ({ name: a.name, quantity: Number(a.quantity) || 1, rate: Number(a.rate) || 0 })),
        })),
        transport_charge: Number(form.transport_charge) || 0,
        transport_gst_rate: Number(form.transport_gst_rate) || 0,
        installation_charge: Number(form.installation_charge) || 0,
        installation_gst_rate: Number(form.installation_gst_rate) || 0,
        advance: Number(form.advance) || 0,
        estimated_cost: grand,
      };
      const { data } = await api.post("/orders", payload);
      setSaved({ id: data.id, order_no: data.id, phone: selectedCust?.phone });
      toast.success(`Order ${data.id} created`);
      nav(`/orders/${data.id}`);
    } catch (e2) { setErr(formatApiError(e2)); toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="new-order-root">
      <div className="flex items-center justify-between">
        <Link to="/orders" className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2" data-testid="back-to-orders">
          <ArrowLeft size={14} /> All Orders
        </Link>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-400">
          <kbd className="px-1.5 py-0.5 border border-zinc-300 rounded font-mono">Ctrl</kbd>
          <span>+</span>
          <kbd className="px-1.5 py-0.5 border border-zinc-300 rounded font-mono">Enter</kbd>
          <span>to save</span>
        </div>
      </div>

      <div className="pb-4 border-b border-[hsl(var(--border))]">
        <div className="overline mb-2 text-[#FF6B00]">Create · Step 1 of 1</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">New Order</h1>
        <p className="text-sm text-muted-foreground mt-1">Add a customer, drop in products, then hit save. Rates and GST update live on the right.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" onKeyDown={(e) => { if (e.ctrlKey && e.key === "Enter") submit(); }}>
        {/* MAIN COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          <SectionCard step={1} title="Customer" subtitle="Search by name, mobile or company. Or add a fresh one on the fly. GSTIN & saved address auto-fill.">
            <CustomerPicker
              customers={customers}
              setCustomers={setCustomers}
              value={form.customer_id}
              onChange={(id, cust) => {
                set("customer_id", id);
                // Autofill shipping_address & remember-me if empty
                if (cust) {
                  const patches = {};
                  if (!form.shipping_address && cust.address) patches.shipping_address = cust.address;
                  if (Object.keys(patches).length) setForm((f) => ({ ...f, ...patches }));
                }
              }}
            />
          </SectionCard>

          <SectionCard step={2} title="Products" subtitle="Search the website catalog to auto-fill options + rates, or add a custom product. Everything is editable.">
            <CatalogPicker
              onPick={(item) => { setItems((p) => [...p, item]); toast.success(`${item.product_name.split(" · ")[0]} added — options auto-filled from catalog`); }}
              onCustom={() => { setItems((p) => [...p, emptyItem()]); toast.success("Custom product added"); }}
            />
            {items.length === 0 ? (
              <div className="p-8 border-2 border-dashed border-zinc-200 rounded-xl text-center">
                <PackagePlus size={26} className="mx-auto text-zinc-400 mb-2" />
                <div className="text-sm text-zinc-500">Search the catalog above or add a custom product.</div>
              </div>
            ) : (
              <OrderItemsEditor items={items} setItems={setItems} testIdPrefix="order-item" />
            )}
          </SectionCard>

          <SectionCard step={3} title="Schedule & Handling" subtitle="Delivery, priority and extras.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={<span className="inline-flex items-center gap-1"><Calendar size={11} /> Order Date</span>}>
                <input type="date" value={form.order_date} onChange={(e) => set("order_date", e.target.value)} className="input" data-testid="order-date-input" />
              </Field>
              <Field label={<span className="inline-flex items-center gap-1"><Calendar size={11} /> Expected Delivery</span>}>
                <input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className="input" data-testid="order-delivery-date-input" />
              </Field>
              <Field label="Priority">
                <div className="flex gap-1">
                  {[
                    { v: "low", l: "Low", cls: "bg-zinc-100 border-zinc-300" },
                    { v: "normal", l: "Normal", cls: "bg-blue-50 border-blue-300" },
                    { v: "high", l: "High", cls: "bg-amber-50 border-amber-400" },
                    { v: "urgent", l: "Urgent", cls: "bg-red-50 border-red-500" },
                  ].map((o) => {
                    const sel = form.priority === o.v;
                    return (
                      <button key={o.v} type="button" onClick={() => set("priority", o.v)} className={`flex-1 px-2 py-2 text-xs font-bold border-2 rounded-lg transition-colors ${sel ? o.cls + " ring-2 ring-offset-1 ring-[#FF6B00]" : "border-zinc-200 hover:border-zinc-400"}`} data-testid={`prio-${o.v}`}>
                        {o.v === "urgent" && "⚡ "}{o.l}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["lamination", "Lamination", null],
                  ["eyelets", "Eyelets", null],
                  ["installation_required", "Installation", <Wrench size={12} key="w" />],
                  ["delivery_required", "Delivery", <Truck size={12} key="t" />],
                ].map(([k, l, ic]) => (
                  <label key={k} className={`flex items-center gap-2 p-2 border-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${form[k] ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-zinc-200 hover:border-zinc-400"}`}>
                    <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="hidden" data-testid={`order-${k}-checkbox`} />
                    {ic}{form[k] && <CheckCircle2 size={12} />} {l}
                  </label>
                ))}
              </div>
            </div>
            <Field label={<span className="inline-flex items-center gap-1"><MapPin size={11} /> Shipping Address (only if delivery required)</span>}>
              <textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} className="input" placeholder="Flat/Shop, Building, Street, City, PIN" data-testid="order-shipping-address" />
            </Field>
            <div className="mt-4">
              <div className="overline mb-2">Transport & Installation</div>
              <ExtrasEditor form={form} set={(patch) => setForm({ ...form, ...patch })} />
            </div>
            <Field label="Remarks (visible to production team)">
              <textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className="input" placeholder="Any special instructions for the job?" data-testid="order-remarks-input" />
            </Field>
          </SectionCard>

          <SectionCard step={4} title="Advance Payment" subtitle="Skip if none was collected upfront.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Amount (₹)">
                <input type="number" value={form.advance} onChange={(e) => set("advance", e.target.value)} className="input" data-testid="order-advance-input" placeholder="0" />
              </Field>
              <Field label="Mode">
                <select value={form.advance_mode} onChange={(e) => set("advance_mode", e.target.value)} className="input" data-testid="order-advance-mode-select">
                  <option value="cash">Cash</option><option value="upi">UPI</option>
                  <option value="card">Card</option><option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </Field>
              <Field label="Payment Date">
                <input type="date" value={form.advance_date} onChange={(e) => set("advance_date", e.target.value)} className="input" data-testid="order-advance-date-input" />
              </Field>
            </div>
          </SectionCard>
        </div>

        {/* STICKY SUMMARY */}
        <aside className="lg:col-span-1">
          <div className="sticky top-6 space-y-4">
            <div className="card-swiss p-5 border-2 border-zinc-900">
              <div className="overline mb-4">Live Summary</div>
              <div className="space-y-2 text-sm">
                <SummaryRow label="Products" value={items.length ? `${items.length} · ${items.reduce((s, i) => s + Number(i.quantity || 0), 0)} pcs` : "—"} />
                <SummaryRow label="Subtotal" value={`₹${Math.round(itemsSub).toLocaleString("en-IN")}`} />
                <SummaryRow label="GST" value={`₹${Math.round(itemsTax + tExtra.tax + iExtra.tax).toLocaleString("en-IN")}`} />
                <SummaryRow label="Extras" value={`₹${Math.round(tExtra.base + iExtra.base).toLocaleString("en-IN")}`} />
                <div className="border-t border-zinc-200 pt-2 mt-2">
                  <SummaryRow bold label="Grand Total" value={<span className="text-[#FF6B00] text-xl">₹{Math.round(grand).toLocaleString("en-IN")}</span>} testid="order-grand-total" />
                </div>
                {Number(form.advance || 0) > 0 && (
                  <>
                    <SummaryRow label="Advance" value={`−₹${Math.round(Number(form.advance) || 0).toLocaleString("en-IN")}`} />
                    <SummaryRow bold label="Balance" value={<span className="text-emerald-700">₹{Math.round(balance).toLocaleString("en-IN")}</span>} />
                  </>
                )}
              </div>

              {selectedCust && (
                <div className="mt-4 p-3 border border-zinc-200 rounded-lg text-xs">
                  <div className="uppercase tracking-widest text-[10px] text-zinc-400 mb-0.5">For</div>
                  <div className="font-bold">{selectedCust.name}</div>
                  <div className="text-zinc-500">{selectedCust.company || ""} · {selectedCust.phone}</div>
                </div>
              )}

              {err && <div className="mt-4 text-xs text-red-600 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="order-form-error">{err}</div>}

              <button type="button" onClick={submit} disabled={saving} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-black px-6 py-3 rounded-full hover:bg-[#e65a00] disabled:opacity-50" data-testid="submit-order-btn">
                {saving ? "Saving…" : (<><Save size={16} /> Create Order</>)}
              </button>
              <Link to="/orders" className="mt-2 block text-center text-xs text-zinc-500 hover:text-[#FF6B00]" data-testid="cancel-order">Cancel and go back</Link>
            </div>

            <div className="text-[11px] text-zinc-500 leading-relaxed p-4 border border-dashed border-zinc-200 rounded-xl">
              <div className="inline-flex items-center gap-1 font-bold text-zinc-700 mb-1"><Sparkles size={11} className="text-[#FF6B00]" /> Tip</div>
              After saving, you can share the WhatsApp invoice, download the PDF and track production stages — all from the order detail page.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* --------------------------- Sub-components -------------------------- */
function SectionCard({ step, title, subtitle, children }) {
  return (
    <div className="card-swiss p-5" data-testid={`section-${step}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#FF6B00] text-white font-black text-sm flex items-center justify-center shrink-0">{step}</div>
        <div>
          <div className="font-display text-xl font-black tracking-tight leading-tight">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, bold, testid }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-zinc-500 ${bold ? "text-zinc-900 font-black" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-black" : ""}`} data-testid={testid}>{value}</span>
    </div>
  );
}

/* Live-searchable customer picker + inline create */
function CustomerPicker({ customers, setCustomers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(null);
  const inputRef = useRef(null);

  const selected = customers.find((c) => c.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 30);
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s)
      || (c.phone || "").includes(q.replace(/\D/g, ""))
      || (c.company || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.gst || "").toLowerCase().includes(s)
      || (c.gstin || "").toLowerCase().includes(s)
    ).slice(0, 40);
  }, [q, customers]);

  const pick = (c) => { onChange(c.id, c); setOpen(false); setQ(""); };

  const saveNew = async () => {
    if (!creating?.name || !creating?.phone) { toast.error("Name and phone are required"); return; }
    try {
      const payload = { ...creating };
      if (payload.gst) payload.gst = payload.gst.toUpperCase().trim();
      const { data } = await api.post("/customers", payload);
      setCustomers((prev) => [data, ...prev]);
      onChange(data.id, data);
      setCreating(null);
      setOpen(false);
      toast.success(`Customer "${data.name}" added (login: ${data.phone}/${data.phone})`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const gstinOf = (c) => (c?.gstin || c?.gst || "").toUpperCase();

  return (
    <div>
      {selected && !open ? (
        <div className="flex items-center justify-between p-3 border-2 border-[#FF6B00] bg-orange-50 rounded-xl" data-testid="selected-customer">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-bold text-sm">{selected.name}</div>
              {gstinOf(selected) && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono" data-testid="selected-gstin-badge">
                  GST · {gstinOf(selected)}
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-500 truncate">
              {selected.company || "—"} · <span className="font-mono">{selected.phone}</span>{selected.email ? ` · ${selected.email}` : ""}
            </div>
            {selected.address && <div className="text-[11px] text-zinc-500 truncate mt-0.5">📍 {selected.address}</div>}
          </div>
          <button type="button" onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} className="text-xs font-bold text-[#FF6B00] hover:underline ml-2 flex-shrink-0" data-testid="change-customer">Change</button>
        </div>
      ) : creating ? (
        <div className="border-2 border-[#FF6B00] bg-orange-50 rounded-xl p-4 space-y-3" data-testid="inline-customer-form">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-1 font-bold text-sm"><UserPlus size={14} className="text-[#FF6B00]" /> New Customer</div>
            <button type="button" onClick={() => setCreating(null)} className="text-zinc-500 hover:text-[#FF6B00]"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input required placeholder="Name *" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} className="input" data-testid="inline-cust-name" autoFocus />
            <input required placeholder="Phone * (this will be their login)" value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} className="input" data-testid="inline-cust-phone" />
            <input placeholder="Company" value={creating.company} onChange={(e) => setCreating({ ...creating, company: e.target.value })} className="input" />
            <input placeholder="Email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} className="input" />
            <input placeholder="GSTIN (optional, 15 chars)" maxLength={15} value={creating.gst} onChange={(e) => setCreating({ ...creating, gst: e.target.value.toUpperCase() })} className="input font-mono uppercase md:col-span-2" data-testid="inline-cust-gstin" />
            <textarea placeholder="Address (auto-fills shipping)" rows={2} value={creating.address} onChange={(e) => setCreating({ ...creating, address: e.target.value })} className="input md:col-span-2" data-testid="inline-cust-address" />
          </div>
          <div className="text-[10px] text-zinc-500">💡 A login account is created automatically. Username & password are both the phone number.</div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(null)} className="btn-swiss btn-outline text-xs">Cancel</button>
            <button type="button" onClick={saveNew} className="btn-swiss btn-accent text-xs" data-testid="save-inline-customer-btn"><Save size={12} /> Save & Select</button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-2 border-2 border-zinc-200 focus-within:border-[#FF6B00] rounded-xl px-3 py-2 bg-white">
            <Search size={14} className="text-zinc-400" />
            <input
              ref={inputRef}
              placeholder="Search by name, mobile, company, GSTIN…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="customer-search"
            />
            <button type="button" onClick={() => setCreating({ name: q || "", phone: "", company: "", email: "", gst: "", address: "" })} className="text-xs font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1" data-testid="add-customer-btn">
              <UserPlus size={12} /> Add
            </button>
          </div>
          {open && (
            <div className="absolute z-20 mt-1 w-full bg-white border-2 border-zinc-200 rounded-xl shadow-lg max-h-80 overflow-y-auto" data-testid="customer-dropdown">
              {filtered.length === 0 ? (
                <div className="p-4 text-center text-sm text-zinc-500">
                  No matches. <button type="button" onClick={() => setCreating({ name: q, phone: "", company: "", email: "", gst: "", address: "" })} className="text-[#FF6B00] font-bold hover:underline">Add "{q}"</button>
                </div>
              ) : (
                filtered.map((c) => {
                  const g = gstinOf(c);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pick(c)}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-zinc-100 last:border-0"
                      data-testid={`cust-opt-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-sm truncate">{c.name}</div>
                        {g && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono flex-shrink-0">B2B</span>}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {c.company || "—"} · <span className="font-mono">{c.phone}</span>{c.email ? ` · ${c.email}` : ""}
                      </div>
                      {g && <div className="text-[10px] font-mono text-emerald-700 truncate mt-0.5">GSTIN: {g}</div>}
                      {c.address && <div className="text-[10px] text-zinc-400 truncate mt-0.5">📍 {c.address}</div>}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
